import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Camera, CheckCircle, AlertCircle, X, Plus, Trash2, Loader2, Cloud, CloudOff, Sparkles, ChevronRight } from 'lucide-react';
import { uploadQueueService } from '../../services/upload-queue.service';
import { openNativeCamera } from '../../utils/camera.utils';
import { Camera as CameraComponent } from '../Camera';
import { isIOS, isNativeApp } from '../../utils/platform.utils';
import { CustomSelect } from '../common/CustomSelect';
import api from '../../api/auth.api';
import type { AsphaltPlant, AsphaltReportTruck } from '../../types/backend.types';
import { useLocation } from '../../contexts/LocationContext';
// Note: Job mismatch modal is handled globally by GlobalMismatchChecker in App.tsx

interface PhotoData {
  file: File; // Store File object instead of base64
  previewUrl: string; // Object URL for preview (memory-efficient)
  caption: string;
  uploadStatus: 'uploading' | 'uploaded' | 'failed';
  uploadedUrl?: string; // URL after successful upload
  thumbnailUrl?: string; // Thumbnail URL after successful upload
}

// Note: TicketData interface removed - tickets are now scanned via TruckTickets module
// and tagged as 'asphalt' in the database

// Job visit interface for multi-job support (weather is auto-populated at 8 PM cron)
interface JobVisit {
  job_id: number;
  job_name: string;
  bradanna_job_number: string;
  arrival_time: string;
  departure_time: string | null;
  crew_count: number;
  total_hours: number;
  ticket_count: number;
  total_tons: number;
}

// Asphalt settings interface
interface AsphaltSettings {
  lunchButtonVisible: boolean;
  lunchMode: 'auto' | 'manual';
  ticketScanQuestion: boolean;
}

// Translation object
const translations = {
  en: {
    asphaltReport: 'Asphalt Report',
    online: 'Online',
    offline: 'Offline - will sync when online',
    reportQueued: 'report queued',
    reportsQueued: 'reports queued',
    reportSaved: 'Report Saved!',
    uploadingInBackground: 'Uploading in background...',
    willUploadWhenOnline: 'Will upload when online',

    // Step names
    workInfo: 'Work Info',
    dumpTrucks: 'Dump Trucks',
    jobSitePhotos: 'Job Site Photos',
    truckTickets: 'Truck Tickets',

    // Ticket scan question
    ticketScanQuestionTitle: 'Ticket Scanning',
    didYouScanAllTickets: 'Did you scan all truck tickets today?',
    yesAllScanned: 'Yes, All Scanned',
    noManualEntry: 'No, Manual Entry Needed',
    ticketsScannedToday: 'tickets scanned today',
    ticketScannedToday: 'ticket scanned today',
    skipDumpTrucks: 'Ticket data will be used from scanned tickets',
    manualEntryRequired: 'Please enter truck information manually',

    // Step 1
    jobSite: 'Job Site',
    autoSelectedFromTime: 'Auto-selected from your current time entry',
    workCompletedToday: 'Work Completed Today',
    describeWhatAccomplished: 'Describe what was accomplished today...',
    suggestEnglish: 'Suggest English',
    translating: 'Translating...',
    jobNeedsForTomorrow: 'Job Needs for Tomorrow',
    whatMaterialsEquipment: 'What materials, equipment, or resources are needed for tomorrow?...',
    asphaltPlant: 'Asphalt Plant',
    selectPlant: 'Select a plant...',
    addNewPlant: '+ Add New Plant',
    enterNewPlantName: 'Enter new plant name...',
    cancel: 'Cancel',
    didCrewTakeLunch: 'Did the crew take lunch today?',
    yes: 'Yes',
    no: 'No',
    notesOptional: 'Notes (Optional)',
    additionalNotes: 'Additional notes or comments...',
    dumpTrailerUsed: 'Dump Trailer Used',
    dumpTrailerDescription: 'Crew picked up material themselves',
    totalTrucks: 'Total Trucks',
    totalLoads: 'Total Loads',
    pleaseEnterTotalTrucks: 'Please enter total trucks',
    pleaseEnterTotalLoads: 'Please enter total loads',
    nextDumpTrucks: 'Next: Dump Trucks',

    // Step 2
    dumpTrucksTitle: 'Dump Trucks',
    dumpTrucksDescription: 'Record dump trucks used today',
    noTrucksAdded: 'No dump trucks added yet. Click "Add Dump Truck" to begin.',
    addDumpTruck: 'Add Dump Truck',
    truckNumber: 'Truck #',
    remove: 'Remove',
    truckNumberPlaceholder: 'e.g., Truck 12 or FL-1234',
    loadsHauled: 'Loads Hauled',
    hoursWorked: 'Hours Worked',
    nextPhotos: 'Next: Job Site Photos',

    // Step 3 - Photos
    jobSitePhotosTitle: 'Job Site Photos',
    jobSitePhotosOptional: 'Job Site Photos (Optional)',
    takePhoto: 'Take Photo',
    noPhotosAdded: 'No photos added yet',
    addCaption: 'Add caption (optional)...',
    nextTickets: 'Next: Truck Tickets',

    // Step 4 - Tickets
    truckTicketsTitle: 'Truck Tickets',
    truckTicketsOptional: 'Truck Tickets (Optional)',
    scanDumpTruckTickets: 'Scan dump truck tickets for your records',
    scanTicket: 'Scan Ticket',
    noTicketsScanned: 'No tickets scanned yet',
    ticket: 'Ticket #',

    // Submit
    submitAsphaltReport: 'Submit Asphalt Report',
    submitting: 'Submitting...',
    confirmSubmit: 'Confirm Submission',
    confirmSubmitMessage: 'Are you sure you want to submit this asphalt report? This action cannot be undone.',
    yesSubmit: 'Yes, Submit Report',

    // Error modal
    error: 'Error',
    ok: 'OK',

    // Validation errors
    mustBeClockedIn: 'You must be clocked in to a job',
    pleaseDescribeWork: 'Please describe the work completed',
    pleaseSelectPlant: 'Please select or enter an asphalt plant',
    pleaseAddTruck: 'Please add at least one dump truck',
    enterTruckNumber: 'Please enter truck number for truck #',
    enterLoadsHauled: 'Please enter loads hauled for truck #',
    enterHoursWorked: 'Please enter hours worked for truck #',

    // Access errors
    accessDenied: 'Access Denied',
    onlyAsphaltForemen: 'Only asphalt foremen can access this module',
    mustBeClockedInToSubmit: 'You must be clocked in to submit an asphalt report',
    userNotLoggedIn: 'User not logged in',
    failedToLoadJob: 'Failed to load current job',
    failedToSubmit: 'Failed to submit report. Please try again.',

    loading: 'Loading...',
  },
  es: {
    asphaltReport: 'Reporte de Asfalto',
    online: 'En línea',
    offline: 'Fuera de línea - se sincronizará cuando esté en línea',
    reportQueued: 'reporte en cola',
    reportsQueued: 'reportes en cola',
    reportSaved: '¡Reporte Guardado!',
    uploadingInBackground: 'Subiendo en segundo plano...',
    willUploadWhenOnline: 'Se subirá cuando esté en línea',

    // Step names
    workInfo: 'Info de Trabajo',
    dumpTrucks: 'Camiones Volquete',
    jobSitePhotos: 'Fotos del Sitio',
    truckTickets: 'Tickets de Camión',

    // Ticket scan question
    ticketScanQuestionTitle: 'Escaneo de Tickets',
    didYouScanAllTickets: '¿Escaneó todos los tickets de camión hoy?',
    yesAllScanned: 'Sí, Todos Escaneados',
    noManualEntry: 'No, Entrada Manual Necesaria',
    ticketsScannedToday: 'tickets escaneados hoy',
    ticketScannedToday: 'ticket escaneado hoy',
    skipDumpTrucks: 'Se usarán los datos de los tickets escaneados',
    manualEntryRequired: 'Por favor ingrese la información del camión manualmente',

    // Step 1
    jobSite: 'Sitio de Trabajo',
    autoSelectedFromTime: 'Seleccionado automáticamente de su entrada de tiempo actual',
    workCompletedToday: 'Trabajo Completado Hoy',
    describeWhatAccomplished: 'Describa lo que se logró hoy...',
    suggestEnglish: 'Sugerir Inglés',
    translating: 'Traduciendo...',
    jobNeedsForTomorrow: 'Necesidades del Trabajo para Mañana',
    whatMaterialsEquipment: '¿Qué materiales, equipos o recursos se necesitan para mañana?...',
    asphaltPlant: 'Planta de Asfalto',
    selectPlant: 'Seleccione una planta...',
    addNewPlant: '+ Agregar Nueva Planta',
    enterNewPlantName: 'Ingrese el nombre de la nueva planta...',
    cancel: 'Cancelar',
    didCrewTakeLunch: '¿La cuadrilla tomó almuerzo hoy?',
    yes: 'Sí',
    no: 'No',
    notesOptional: 'Notas (Opcional)',
    additionalNotes: 'Notas o comentarios adicionales...',
    dumpTrailerUsed: 'Remolque de Descarga Usado',
    dumpTrailerDescription: 'La cuadrilla recogió material ellos mismos',
    totalTrucks: 'Total de Camiones',
    totalLoads: 'Total de Cargas',
    pleaseEnterTotalTrucks: 'Por favor ingrese el total de camiones',
    pleaseEnterTotalLoads: 'Por favor ingrese el total de cargas',
    nextDumpTrucks: 'Siguiente: Camiones Volquete',

    // Step 2
    dumpTrucksTitle: 'Camiones Volquete',
    dumpTrucksDescription: 'Registre los camiones volquete utilizados hoy',
    noTrucksAdded: 'No se han agregado camiones volquete. Haga clic en "Agregar Camión Volquete" para comenzar.',
    addDumpTruck: 'Agregar Camión Volquete',
    truckNumber: 'Camión #',
    remove: 'Eliminar',
    truckNumberPlaceholder: 'ej., Camión 12 o FL-1234',
    loadsHauled: 'Cargas Transportadas',
    hoursWorked: 'Horas Trabajadas',
    nextPhotos: 'Siguiente: Fotos del Sitio',

    // Step 3 - Photos
    jobSitePhotosTitle: 'Fotos del Sitio de Trabajo',
    jobSitePhotosOptional: 'Fotos del Sitio de Trabajo (Opcional)',
    takePhoto: 'Tomar Foto',
    noPhotosAdded: 'No se han agregado fotos',
    addCaption: 'Agregar leyenda (opcional)...',
    nextTickets: 'Siguiente: Tickets de Camión',

    // Step 4 - Tickets
    truckTicketsTitle: 'Tickets de Camión',
    truckTicketsOptional: 'Tickets de Camión (Opcional)',
    scanDumpTruckTickets: 'Escanee tickets de camión volquete para sus registros',
    scanTicket: 'Escanear Ticket',
    noTicketsScanned: 'No se han escaneado tickets',
    ticket: 'Ticket #',

    // Submit
    submitAsphaltReport: 'Enviar Reporte de Asfalto',
    submitting: 'Enviando...',
    confirmSubmit: 'Confirmar Envío',
    confirmSubmitMessage: '¿Está seguro de que desea enviar este reporte de asfalto? Esta acción no se puede deshacer.',
    yesSubmit: 'Sí, Enviar Reporte',

    // Error modal
    error: 'Error',
    ok: 'OK',

    // Validation errors
    mustBeClockedIn: 'Debe estar registrado en un trabajo',
    pleaseDescribeWork: 'Por favor describa el trabajo completado',
    pleaseSelectPlant: 'Por favor seleccione o ingrese una planta de asfalto',
    pleaseAddTruck: 'Por favor agregue al menos un camión volquete',
    enterTruckNumber: 'Por favor ingrese el número de camión para camión #',
    enterLoadsHauled: 'Por favor ingrese cargas transportadas para camión #',
    enterHoursWorked: 'Por favor ingrese horas trabajadas para camión #',

    // Access errors
    accessDenied: 'Acceso Denegado',
    onlyAsphaltForemen: 'Solo los capataces de asfalto pueden acceder a este módulo',
    mustBeClockedInToSubmit: 'Debe estar registrado para enviar un reporte de asfalto',
    userNotLoggedIn: 'Usuario no conectado',
    failedToLoadJob: 'Error al cargar el trabajo actual',
    failedToSubmit: 'Error al enviar el reporte. Por favor intente de nuevo.',

    loading: 'Cargando...',
  }
};

const API_BASE_URL = import.meta.env.VITE_API_URL;

const AsphaltReports: React.FC = () => {
  const navigate = useNavigate();

  // Language
  const [language, setLanguage] = useState<'en' | 'es'>('en');
  const t = translations[language];

  // Asphalt settings from backend
  const [settings, setSettings] = useState<AsphaltSettings>({
    lunchButtonVisible: true,
    lunchMode: 'auto',
    ticketScanQuestion: true
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Ticket scan question state (Step 0)
  const [ticketsScanned, setTicketsScanned] = useState<boolean | null>(null);
  const [asphaltTicketCount, setAsphaltTicketCount] = useState<number>(0);

  // Current step (0 = ticket question, 1 = work info, 2 = dump trucks, 3 = photos)
  const [step, setStep] = useState(0);

  // User data
  const [user, setUser] = useState<any>(null);

  // Job data (auto-selected from clock-in)
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [selectedJobName, setSelectedJobName] = useState<string>('');
  const [jobLoading, setJobLoading] = useState(true);
  const [existingReportId, setExistingReportId] = useState<number | null>(null);

  // Multi-job support
  const [crewJobs, setCrewJobs] = useState<JobVisit[]>([]);
  const [isMultiJob, setIsMultiJob] = useState(false);
  const [jobVisits, setJobVisits] = useState<JobVisit[]>([]);

  // Form fields
  const [workDescription, setWorkDescription] = useState('');
  const [jobNeeds, setJobNeeds] = useState('');
  const [crewTookLunch, setCrewTookLunch] = useState<boolean | null>(null);
  const [notes, setNotes] = useState('');

  // Dump trailer (weather is now auto-populated at 8 PM cron)
  const [dumpTrailerUsed, setDumpTrailerUsed] = useState(false);

  // Material suppliers (from subcontractors table)
  const [materialSuppliers, setMaterialSuppliers] = useState<{subcontractor_id: number; company_name: string; preferred: boolean}[]>([]);
  const [suppliersLoaded, setSuppliersLoaded] = useState(false);
  const [pendingSupplierId, setPendingSupplierId] = useState<number | null>(null); // For race condition fix
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(null);
  const [noMaterialToday, setNoMaterialToday] = useState(false);
  const [showNewSupplierInput, setShowNewSupplierInput] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');

  // Manual totals
  const [totalTrucks, setTotalTrucks] = useState<string>('');
  const [totalLoads, setTotalLoads] = useState<string>('');

  // Trucks
  const [trucks, setTrucks] = useState<AsphaltReportTruck[]>([]);

  // Photos
  const [photos, setPhotos] = useState<PhotoData[]>([]);
  const [photoCaption, setPhotoCaption] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  // Pre-loaded standalone photos (taken earlier via PhotoGallery, grouped by job)
  interface PreloadedPhoto {
    photo_id: number;
    photo_url: string;
    thumbnail_url: string | null;
    caption: string | null;
    job_id: number;
    job_name: string;
    bradanna_job_number: string;
    created_at: string;
  }
  const [preloadedPhotos, setPreloadedPhotos] = useState<PreloadedPhoto[]>([]);

  // Note: Ticket scanning is now handled via TruckTickets module
  // We only track the count from API (asphaltTicketCount state)

  // Translation/AI
  const [isTranslating, setIsTranslating] = useState(false);
  const [showTranslatedIndicator, setShowTranslatedIndicator] = useState(false);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [uploadQueueCount, setUploadQueueCount] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<'uploading' | 'completed' | 'failed' | 'timeout'>('uploading');

  // Note: Job mismatch modal is handled globally by GlobalMismatchChecker in App.tsx
  const [jobs, setJobs] = useState<any[]>([]);

  // Get location for stale check
  const { location } = useLocation();

  // Track if component has completed initial mount (to avoid redirect race condition)
  const [hasCompletedMount, setHasCompletedMount] = useState(false);

  // Mark component as mounted after a short delay to let location context propagate
  useEffect(() => {
    const timer = setTimeout(() => setHasCompletedMount(true), 500);
    return () => clearTimeout(timer);
  }, []);

  // Listen for page visibility changes (iPad sleep/wake, tab switch) - redirect on wake if stale
  useEffect(() => {
    if (isNativeApp()) return; // Native apps handle this in LocationContext

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('AsphaltReports: Page became visible, checking location staleness');
        // Check staleness directly here to avoid race conditions
        if (!location || (Date.now() - location.timestamp) > 5 * 60 * 1000) {
          console.log('AsphaltReports: Location stale after wake, redirecting to dashboard');
          navigate('/');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [location, navigate]);

  // Check for stale location (> 5 minutes old) - PWA only, redirect to dashboard
  // Only redirect after mount completes to avoid race with location context update
  const STALE_THRESHOLD = 5 * 60 * 1000; // 5 minutes
  const isLocationStale = useMemo(() => {
    if (isNativeApp()) return false; // Native apps auto-refresh on resume
    if (!location) return true;
    return (Date.now() - location.timestamp) > STALE_THRESHOLD;
  }, [location]);

  // Redirect to dashboard if location is stale on PWA (no form data to preserve)
  // Wait for mount to complete to avoid race condition with location context update
  useEffect(() => {
    if (isLocationStale && !isNativeApp() && !jobLoading && hasCompletedMount) {
      console.log('AsphaltReports: Location stale on mount check, redirecting to dashboard');
      navigate('/');
    }
  }, [isLocationStale, jobLoading, hasCompletedMount, navigate]);

  // Note: Job mismatch detection is handled globally by GlobalMismatchChecker in App.tsx

  // Listen for language changes from Header
  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      const newLang = (event as CustomEvent).detail;
      if (newLang === 'en' || newLang === 'es') {
        setLanguage(newLang);
      }
    };

    window.addEventListener('languageChange', handleLanguageChange);
    const savedLanguage = localStorage.getItem('language') as 'en' | 'es' | null;
    if (savedLanguage === 'en' || savedLanguage === 'es') {
      setLanguage(savedLanguage);
    }

    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);

  // Scroll to top when step changes
  useEffect(() => {
    const mainContent = document.querySelector('.main-content');
    if (mainContent) {
      mainContent.scrollTo(0, 0);
    }
    window.scrollTo(0, 0);
  }, [step]);

  // Load asphalt report settings
  const loadAsphaltSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/settings/public/asphalt-report`);
      const data = await response.json();
      if (data.success) {
        setSettings({
          lunchButtonVisible: data.data.lunch_button_visible,
          lunchMode: data.data.lunch_mode,
          ticketScanQuestion: data.data.ticket_scan_question
        });
        // If ticket scan question is disabled, skip step 0 and go to step 1
        if (!data.data.ticket_scan_question) {
          setStep(1);
        }
      }
    } catch (err) {
      console.error('Error loading asphalt settings:', err);
      // Use defaults on error, skip step 0 to avoid blocking
      setStep(1);
    } finally {
      setSettingsLoaded(true);
    }
  };

  // Load asphalt ticket count for current job and auto-populate totalLoads
  const loadAsphaltTicketCount = async (jobId: number) => {
    try {
      const todayStr = getReportDateString();

      const response = await api.get(`/api/trucks/asphalt?job_id=${jobId}&date=${todayStr}`);
      const data = response.data;
      if (data.success) {
        setAsphaltTicketCount(data.data.count || data.data.length || 0);

        // Auto-populate totalLoads from scanned tickets (only if not already set)
        if (data.data.total_loads != null && !totalLoads) {
          setTotalLoads(String(data.data.total_loads));
          console.log(`📊 Auto-populated totalLoads = ${data.data.total_loads} (${data.data.duplicates_removed || 0} duplicates removed)`);
        }
      }
    } catch (err) {
      console.error('Error loading asphalt tickets:', err);
    }
  };

  // Load crew's jobs for the day (multi-job support)
  const loadCrewJobs = async () => {
    try {
      const todayStr = getReportDateString();

      const response = await api.get(`/api/asphalt-reports/crew-jobs/${todayStr}`);
      const data = response.data;
      if (data.success) {
        console.log('📍 Crew jobs for today:', data.data);
        setCrewJobs(data.data.jobs || []);
        setIsMultiJob(data.data.is_multi_job || false);

        // Initialize jobVisits (weather is auto-populated at 8 PM cron)
        if (data.data.jobs && data.data.jobs.length > 0) {
          const visits = data.data.jobs.map((job: any, index: number) => ({
            ...job,
            display_order: index
          }));
          setJobVisits(visits);
        }
      }
    } catch (err) {
      console.error('Error loading crew jobs:', err);
    }
  };

  // Load user on mount
  useEffect(() => {
    const userJson = localStorage.getItem('bfa_user');
    if (userJson) {
      const parsedUser = JSON.parse(userJson);
      setUser(parsedUser);

      // Check if user is asphalt_foreman
      if (parsedUser.role !== 'asphalt_foreman' && parsedUser.role !== 'admin') {
        setError(t.onlyAsphaltForemen);
        setJobLoading(false);
        return;
      }

      loadMaterialSuppliers();
      loadJobs(); // For mismatch detection
      loadCurrentJob();
      loadAsphaltSettings();
      loadCrewJobs(); // Multi-job support
      loadTodaysPhotos(); // Load standalone photos taken earlier via PhotoGallery
    } else {
      setError(t.userNotLoggedIn);
      setJobLoading(false);
    }
  }, []);

  // Load asphalt ticket count when job is selected
  useEffect(() => {
    if (selectedJobId) {
      loadAsphaltTicketCount(selectedJobId);
    }
  }, [selectedJobId]);

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // iPad scroll fix: scroll focused input into view after keyboard opens
  useEffect(() => {
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        setTimeout(() => {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    };
    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, []);

  // Auto-save/load draft functionality
  const saveDraftToLocalStorage = () => {
    if (!selectedJobId || !user?.employee_id) return;

    const draft = {
      jobId: selectedJobId,
      jobName: selectedJobName,
      workDescription,
      jobNeeds,
      notes,
      crewTookLunch,
      selectedSupplierId,
      noMaterialToday,
      newSupplierName,
      trucks,
      ticketsScanned, // Whether tickets were scanned via TruckTickets module
      photoCount: photos.length,
      photoCaptions: photos.map(p => p.caption || ''),
      step,
      // Dump trailer (weather is auto-populated at 8 PM cron)
      dumpTrailerUsed,
      // Total trucks/loads
      totalTrucks,
      totalLoads,
      // Multi-job fields
      isMultiJob,
      jobVisits,
      timestamp: Date.now()
    };

    const key = `asphalt_draft_${user.employee_id}_${selectedJobId}`;
    localStorage.setItem(key, JSON.stringify(draft));
    console.log('💾 Asphalt report draft saved');
  };

  const loadDraftFromLocalStorage = () => {
    if (!selectedJobId || !user?.employee_id) return;

    const key = `asphalt_draft_${user.employee_id}_${selectedJobId}`;
    const savedDraft = localStorage.getItem(key);

    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);

        // Check if draft is from today (within 24 hours)
        const draftAge = Date.now() - (draft.timestamp || 0);
        const oneDayMs = 24 * 60 * 60 * 1000;

        if (draftAge > oneDayMs) {
          console.log('📋 Draft is older than 24 hours, ignoring');
          localStorage.removeItem(key);
          return;
        }

        console.log('📋 Loading asphalt report draft:', draft);

        setWorkDescription(draft.workDescription || '');
        setJobNeeds(draft.jobNeeds || '');
        setNotes(draft.notes || '');
        setCrewTookLunch(draft.crewTookLunch);
        // Handle supplier ID with pending mechanism (same as database load)
        if (draft.selectedSupplierId) {
          if (suppliersLoaded) {
            setSelectedSupplierId(draft.selectedSupplierId);
          } else {
            setPendingSupplierId(draft.selectedSupplierId);
          }
        }
        setNoMaterialToday(draft.noMaterialToday || false);
        setNewSupplierName(draft.newSupplierName || '');
        // Dump trailer (weather is auto-populated at 8 PM cron)
        if (draft.dumpTrailerUsed !== undefined) setDumpTrailerUsed(draft.dumpTrailerUsed || false);
        // Total trucks/loads
        if (draft.totalTrucks !== undefined) setTotalTrucks(draft.totalTrucks);
        if (draft.totalLoads !== undefined) setTotalLoads(draft.totalLoads);
        // Multi-job fields
        if (draft.isMultiJob !== undefined) setIsMultiJob(draft.isMultiJob || false);
        if (draft.jobVisits !== undefined) setJobVisits(draft.jobVisits || []);
        setTrucks(draft.trucks || []);
        if (draft.ticketsScanned !== undefined) {
          setTicketsScanned(draft.ticketsScanned);
        }
        setStep(draft.step || (settings.ticketScanQuestion ? 0 : 1));

        // Note: Can't restore actual photo File objects from localStorage
        // Photos that were queued will still be in upload queue
        // User may need to re-add photos if they navigate away before upload completes

      } catch (err) {
        console.error('Error loading draft:', err);
        localStorage.removeItem(key);
      }
    }
  };

  const clearDraftFromLocalStorage = () => {
    if (!selectedJobId || !user?.employee_id) return;
    const key = `asphalt_draft_${user.employee_id}_${selectedJobId}`;
    localStorage.removeItem(key);
    console.log('🗑️ Asphalt report draft cleared');
  };

  // Load data when job is selected (database first, then local draft as fallback)
  useEffect(() => {
    const loadExistingData = async () => {
      if (!selectedJobId || !user) return;

      // First try to load from database (authoritative source)
      const loadedFromDb = await loadTodaysReport();

      // If no database entry, try local draft
      if (!loadedFromDb) {
        loadDraftFromLocalStorage();
      }
    };

    loadExistingData();
  }, [selectedJobId, user]);

  // Auto-save every 30 seconds
  useEffect(() => {
    if (!selectedJobId) return;

    const autoSaveInterval = setInterval(() => {
      if (workDescription.trim() || trucks.length > 0 ||
          photos.length > 0 || jobNeeds.trim() || notes.trim()) {
        saveDraftToLocalStorage();
      }
    }, 30000); // 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [selectedJobId, workDescription, jobNeeds, notes, crewTookLunch, selectedSupplierId,
      noMaterialToday, newSupplierName, trucks, photos, step, user, ticketsScanned]);

  // Save draft on beforeunload (browser close/refresh)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!success && selectedJobId && (workDescription.trim() || trucks.length > 0 ||
          photos.length > 0)) {
        saveDraftToLocalStorage();
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [selectedJobId, workDescription, trucks, photos, success]);

  // Save draft when component unmounts
  useEffect(() => {
    return () => {
      if (!success && selectedJobId && (workDescription.trim() || trucks.length > 0 ||
          photos.length > 0)) {
        console.log('💾 Auto-saving draft on navigation...');
        saveDraftToLocalStorage();
      }
    };
  }, [success, selectedJobId, workDescription, trucks, photos]);

  // Monitor upload queue
  useEffect(() => {
    const updateQueueCount = async () => {
      const stats = await uploadQueueService.getQueueStats();
      setUploadQueueCount(stats.total);
    };

    updateQueueCount();
    const interval = setInterval(updateQueueCount, 2000);

    return () => clearInterval(interval);
  }, []);

  // Reset upload status when success is cleared
  useEffect(() => {
    if (!success) {
      setUploadStatus('uploading');
    }
  }, [success]);

  // Load jobs for mismatch detection
  const loadJobs = async () => {
    try {
      const response = await api.get('/api/jobs');
      if (response.data.success) {
        setJobs(response.data.data.filter((j: any) => j.is_active));
      }
    } catch (err) {
      console.error('Error loading jobs:', err);
    }
  };

  // Load current job from time entry
  const loadCurrentJob = async () => {
    try {
      setJobLoading(true);
      const response = await api.get('/api/time/current');
      const data = response.data;

      if (data.success && data.data?.job_id) {
        console.log('Auto-selected job from time entry:', data.data.job_id);
        setSelectedJobId(data.data.job_id);
        setSelectedJobName(data.data.job_name || '');
        // Note: Job mismatch detection is handled globally by GlobalMismatchChecker
      } else {
        setError(t.mustBeClockedInToSubmit);
      }
    } catch (err) {
      console.error('Error loading current job:', err);
      setError(t.failedToLoadJob);
    } finally {
      setJobLoading(false);
    }
  };

  // Load today's standalone asphalt photos (taken via PhotoGallery before opening this form)
  const loadTodaysPhotos = async () => {
    try {
      const response = await api.get('/api/asphalt-reports/photos/today');
      if (response.data.success) {
        console.log(`📷 Loaded ${response.data.data.length} pre-existing asphalt photos for today`);
        setPreloadedPhotos(response.data.data);
      }
    } catch (err) {
      console.error('Error loading today\'s asphalt photos:', err);
    }
  };

  // Load asphalt plants (from subcontractors with asphalt_plant type)
  const loadMaterialSuppliers = async () => {
    try {
      // Use public endpoint - no auth required
      const response = await fetch(`${API_BASE_URL}/api/subcontractors/search/asphalt_plant`);
      const data = await response.json();

      if (data.success) {
        // Sort preferred suppliers first
        const sorted = data.data.sort((a: any, b: any) => {
          if (a.preferred && !b.preferred) return -1;
          if (!a.preferred && b.preferred) return 1;
          return a.company_name.localeCompare(b.company_name);
        });
        setMaterialSuppliers(sorted);
        setSuppliersLoaded(true);
      }
    } catch (err) {
      console.error('Error loading material suppliers:', err);
      setSuppliersLoaded(true); // Still mark as loaded so we don't hang
    }
  };

  // Apply pending supplier ID once suppliers are loaded (fixes race condition)
  useEffect(() => {
    if (suppliersLoaded && pendingSupplierId !== null) {
      console.log('🔗 Applying pending supplier ID:', pendingSupplierId);
      setSelectedSupplierId(pendingSupplierId);
      setPendingSupplierId(null);
    }
  }, [suppliersLoaded, pendingSupplierId]);

  // Cleanup object URLs on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      photos.forEach(photo => {
        if (photo.previewUrl && photo.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(photo.previewUrl);
        }
      });
    };
  }, []);

  // Spanish to English translation using Azure AI
  const handleTranslate = async () => {
    if (!workDescription.trim()) return;

    setIsTranslating(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/improve-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        },
        body: JSON.stringify({ text: workDescription })
      });

      const data = await response.json();

      if (data.success && data.data?.improved) {
        setWorkDescription(data.data.improved);

        // Show translation indicator
        setShowTranslatedIndicator(true);

        // Hide indicator after 3 seconds
        setTimeout(() => {
          setShowTranslatedIndicator(false);
        }, 3000);
      }
    } catch (err) {
      console.error('Translation error:', err);
      // Silently fail - don't disrupt user flow
    } finally {
      setIsTranslating(false);
    }
  };

  // Add truck
  const addTruck = () => {
    setTrucks([...trucks, { truck_number: '', loads_hauled: 0, hours_worked: 0 }]);
  };

  // Remove truck
  const removeTruck = (index: number) => {
    setTrucks(trucks.filter((_, i) => i !== index));
  };

  // Update truck field
  const updateTruck = (index: number, field: keyof AsphaltReportTruck, value: string | number) => {
    const updated = [...trucks];
    updated[index] = { ...updated[index], [field]: value };
    setTrucks(updated);
  };

  // Compress image to reduce upload size (max 800x800, JPEG 0.7 quality)
  const compressImage = async (file: File): Promise<File> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 800;
          let width = img.width;
          let height = img.height;
          if (width > height) {
            if (width > MAX_WIDTH) { height *= MAX_WIDTH / width; width = MAX_WIDTH; }
          } else {
            if (height > MAX_HEIGHT) { width *= MAX_HEIGHT / height; height = MAX_HEIGHT; }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            resolve(new File([blob!], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', 0.7);
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });
  };

  // Handle photo capture (iOS/native camera)
  const handlePhotoInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // Compress image before upload
      const compressed = await compressImage(file);
      // Use object URL instead of base64 for memory efficiency
      const previewUrl = URL.createObjectURL(compressed);
      // Add photo with 'uploading' status and start background upload immediately
      setPhotos(prev => [...prev, { file: compressed, previewUrl, caption: '', uploadStatus: 'uploading' }]);
      // Start background upload
      uploadPhotoInBackground(compressed, previewUrl);
    } catch (err) {
      console.error('Error processing photo:', err);
      setError('Failed to process photo');
    }

    // Reset input
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  // Handle camera photo (web camera)
  const handleCameraPhoto = async (dataUrl: string) => {
    setShowCamera(false);

    try {
      // Convert dataUrl to Blob then File
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const rawFile = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });

      // Compress image before upload
      const compressed = await compressImage(rawFile);
      // Use object URL instead of base64 for memory efficiency
      const previewUrl = URL.createObjectURL(compressed);
      // Add photo with 'uploading' status and start background upload immediately
      setPhotos(prev => [...prev, { file: compressed, previewUrl, caption: '', uploadStatus: 'uploading' }]);
      // Start background upload
      uploadPhotoInBackground(compressed, previewUrl);
    } catch (err) {
      console.error('Error processing photo:', err);
      setError('Failed to process photo');
    }
  };

  // Update photo caption
  const updatePhotoCaption = (index: number, caption: string) => {
    const updated = [...photos];
    updated[index].caption = caption;
    setPhotos(updated);
  };

  // Remove photo
  const removePhoto = (index: number) => {
    // Revoke object URL to free memory
    const photoToRemove = photos[index];
    if (photoToRemove && photoToRemove.previewUrl && photoToRemove.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(photoToRemove.previewUrl);
    }

    setPhotos(photos.filter((_, i) => i !== index));
  };

  // Upload photo in background immediately when taken - uses asphalt photos endpoint
  const uploadPhotoInBackground = async (file: File, previewUrl: string) => {
    if (!selectedJobId) {
      console.error('❌ Cannot upload photo: no job selected');
      setPhotos(prev => prev.map(p =>
        p.previewUrl === previewUrl
          ? { ...p, uploadStatus: 'failed' as const }
          : p
      ));
      return;
    }

    try {
      console.log(`📤 Starting asphalt photo upload: ${file.name} for job ${selectedJobId}`);

      const formData = new FormData();
      formData.append('photos', file); // Field name must be 'photos' to match multer config
      formData.append('job_id', selectedJobId.toString());

      // Upload to asphalt-reports photos endpoint (stores in ASPHALT_REPORT_PHOTOS with job_id)
      const response = await fetch(`${API_BASE_URL}/api/asphalt-reports/photos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success && data.data.length > 0) {
        const uploadedPhoto = data.data[0];
        console.log(`✅ Asphalt photo upload complete: ${file.name} -> ${uploadedPhoto.fullUrl}`);
        // Update the photo entry with uploaded URL and thumbnail
        setPhotos(prev => prev.map(p =>
          p.previewUrl === previewUrl
            ? { ...p, uploadStatus: 'uploaded' as const, uploadedUrl: uploadedPhoto.fullUrl, thumbnailUrl: uploadedPhoto.thumbnailUrl }
            : p
        ));

        // Refresh preloaded photos to include this new one
        loadTodaysPhotos();
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (err: any) {
      console.error(`❌ Asphalt photo upload error: ${file.name}`, err.message || err);
      // Mark as failed - will be uploaded via queue at submit time
      setPhotos(prev => prev.map(p =>
        p.previewUrl === previewUrl
          ? { ...p, uploadStatus: 'failed' as const }
          : p
      ));
    }
  };

  // Helper function to get the report date in EST timezone
  // Night work window (8 PM - 7 AM): report_date = next day (aligns with 8 PM cron)
  const getReportDateString = (): string => {
    const nowEST = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const hourEST = nowEST.getHours();

    // For 8 PM - midnight, use tomorrow's date
    if (hourEST >= 20) {
      nowEST.setDate(nowEST.getDate() + 1);
    }

    const year = nowEST.getFullYear();
    const month = String(nowEST.getMonth() + 1).padStart(2, '0');
    const day = String(nowEST.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Detect if current time is in the night work window
  const getNightWorkStatus = () => {
    const nowEST = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const hourEST = nowEST.getHours();
    return hourEST >= 20 || hourEST < 7;
  };

  // Load today's report from database (cross-device editing)
  const loadTodaysReport = async (): Promise<boolean> => {
    if (!selectedJobId || !user) return false;

    try {
      // Get report date in EST timezone (night work window shifts date forward)
      const todayStr = getReportDateString();

      console.log(`📡 Checking database for asphalt report: job_id=${selectedJobId}, date=${todayStr}`);

      const response = await api.get(`/api/asphalt-reports?job_id=${selectedJobId}`);
      const data = response.data;

      if (data.success && data.data.length > 0) {
        // Find today's report by current user
        const myReport = data.data.find((r: any) =>
          r.foreman_id === user.employee_id &&
          r.report_date?.startsWith(todayStr)
        );

        if (myReport) {
          console.log('✅ Found existing asphalt report in database:', myReport.report_id);
          setExistingReportId(myReport.report_id);

          // Load full report details
          const detailResponse = await api.get(`/api/asphalt-reports/${myReport.report_id}`);
          const reportData = detailResponse.data;

          if (reportData.success && reportData.data) {
            const report = reportData.data;
            console.log('📥 Loading asphalt report data:', report);

            // Pre-fill form fields
            if (report.work_description) setWorkDescription(report.work_description);
            if (report.job_needs) setJobNeeds(report.job_needs);
            if (report.crew_took_lunch !== null && report.crew_took_lunch !== undefined) {
              setCrewTookLunch(report.crew_took_lunch === 1 || report.crew_took_lunch === true);
            }
            // Support both old (asphalt_plant_id) and new (material_supplier_id) field names
            const supplierId = report.material_supplier_id || report.asphalt_plant_id;
            if (supplierId) {
              // If suppliers already loaded, set directly; otherwise queue for when they load
              if (suppliersLoaded) {
                console.log('🔗 Setting supplier ID directly:', supplierId);
                setSelectedSupplierId(supplierId);
              } else {
                console.log('⏳ Queuing supplier ID (waiting for suppliers to load):', supplierId);
                setPendingSupplierId(supplierId);
              }
            }
            if (report.notes) setNotes(report.notes);

            // Load total trucks/loads
            if (report.total_trucks !== null && report.total_trucks !== undefined) setTotalTrucks(String(report.total_trucks));
            if (report.total_loads !== null && report.total_loads !== undefined) setTotalLoads(String(report.total_loads));

            // Load trucks
            if (report.trucks && report.trucks.length > 0) {
              const loadedTrucks = report.trucks.map((t: any) => ({
                truck_number: t.truck_number || '',
                loads_hauled: t.loads_hauled || 0,
                hours_worked: t.hours_worked || 0
              }));
              setTrucks(loadedTrucks);
            }

            // Load photos from existing report (already uploaded to Azure)
            if (report.photos && report.photos.length > 0) {
              const loadedPhotos: PhotoData[] = report.photos.map((p: any) => ({
                file: new File([], 'existing-photo'), // Placeholder file for already-uploaded photos
                previewUrl: p.photo_url || p.thumbnail_url,
                caption: p.caption || '',
                uploadStatus: 'uploaded' as const,
                uploadedUrl: p.photo_url,
                thumbnailUrl: p.thumbnail_url
              }));
              setPhotos(loadedPhotos);
              console.log(`📷 Loaded ${loadedPhotos.length} existing photos from report`);
            }

            // Load dump trailer status
            if (report.dump_trailer_used !== undefined) {
              setDumpTrailerUsed(report.dump_trailer_used === 1 || report.dump_trailer_used === true);
            }

            // Load job visits for multi-job reports
            if (report.job_visits) {
              try {
                const visits = typeof report.job_visits === 'string'
                  ? JSON.parse(report.job_visits)
                  : report.job_visits;
                if (Array.isArray(visits) && visits.length > 0) {
                  setJobVisits(visits);
                  setIsMultiJob(visits.length > 1);
                }
              } catch (e) {
                console.error('Error parsing job_visits:', e);
              }
            }

            return true; // Data loaded from database
          }
        }
      }
      return false; // No data found in database
    } catch (err) {
      console.error('Error loading asphalt report:', err);
      // Don't show error to user - just continue with empty form
      return false;
    }
  };

  // Reset form
  const resetForm = () => {
    // Go back to step 0 if ticket scan question is enabled, else step 1
    setStep(settings.ticketScanQuestion ? 0 : 1);
    setWorkDescription('');
    setJobNeeds('');
    setCrewTookLunch(null);
    setSelectedSupplierId(null);
    setNoMaterialToday(false);
    setShowNewSupplierInput(false);
    setNewSupplierName('');
    setNotes('');
    setTrucks([]);
    setPhotos([]);
    setTicketsScanned(null);
    setPhotoCaption('');
    setExistingReportId(null);
    // Dump trailer (weather is auto-populated at 8 PM cron)
    setDumpTrailerUsed(false);
    // Multi-job fields
    setJobVisits([]);
    setIsMultiJob(false);
    setCrewJobs([]);
  };

  // Navigate to next step
  const goToNextStep = async () => {
    // Step 0: Ticket scan question - handled by button clicks directly
    if (step === 0) {
      // This shouldn't be called from step 0, handled by button clicks
      setStep(1);
      const mc = document.querySelector('.main-content');
      if (mc) mc.scrollTo(0, 0); else window.scrollTo(0, 0);
      return;
    }

    // Validate current step before proceeding
    if (step === 1) {
      if (!selectedJobId) {
        setError(t.mustBeClockedIn);
        return;
      }
      if (!workDescription.trim()) {
        setError(t.pleaseDescribeWork);
        return;
      }
      if (!noMaterialToday && !selectedSupplierId && !newSupplierName.trim()) {
        setError(t.pleaseSelectPlant);
        return;
      }
      // Validate total trucks/loads when material supplier selected and no dump trailer
      if (!dumpTrailerUsed && !noMaterialToday && (selectedSupplierId || newSupplierName.trim())) {
        if (!totalTrucks || parseInt(totalTrucks) <= 0) {
          setError(t.pleaseEnterTotalTrucks);
          return;
        }
        if (!totalLoads || parseInt(totalLoads) <= 0) {
          setError(t.pleaseEnterTotalLoads);
          return;
        }
      }
      // Only validate lunch if lunch button is visible
      if (settings.lunchButtonVisible && crewTookLunch === null) {
        setError(t.didCrewTakeLunch);
        return;
      }

      // Auto-translate work description and job needs from Spanish to English
      console.log('🔄 Starting auto-translation on step change...');
      setIsTranslating(true);
      try {
        // Translate work description if needed
        if (workDescription.trim()) {
          console.log('📝 Translating work description:', workDescription);
          const workResponse = await fetch(`${API_BASE_URL}/api/ai/improve-text`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
            },
            body: JSON.stringify({ text: workDescription })
          });

          const workData = await workResponse.json();
          console.log('✅ Work description translation response:', workData);
          if (workData.success && workData.data?.improved && workData.data?.was_spanish) {
            console.log('🔁 Updating work description from Spanish to English');
            setWorkDescription(workData.data.improved);
          } else {
            console.log('ℹ️ Work description not Spanish or translation not needed');
          }
        }

        // Translate job needs if needed
        if (jobNeeds.trim()) {
          console.log('📝 Translating job needs:', jobNeeds);
          const needsResponse = await fetch(`${API_BASE_URL}/api/ai/improve-text`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
            },
            body: JSON.stringify({ text: jobNeeds })
          });

          const needsData = await needsResponse.json();
          console.log('✅ Job needs translation response:', needsData);
          if (needsData.success && needsData.data?.improved && needsData.data?.was_spanish) {
            console.log('🔁 Updating job needs from Spanish to English');
            setJobNeeds(needsData.data.improved);
          } else {
            console.log('ℹ️ Job needs not Spanish or translation not needed');
          }
        }
      } catch (err) {
        console.error('❌ Auto-translation error:', err);
        // Continue anyway - don't block progression
      } finally {
        console.log('✅ Auto-translation complete');
        setIsTranslating(false);
      }

      // If tickets were scanned, skip dump trucks (step 2) and go to photos (step 3)
      if (ticketsScanned === true) {
        setError(null);
        setStep(3);
        const mc = document.querySelector('.main-content');
        if (mc) mc.scrollTo(0, 0); else window.scrollTo(0, 0);
        return;
      }
    } else if (step === 2) {
      // Validate trucks - required if ticketsScanned is false
      if (ticketsScanned === false && trucks.length === 0) {
        setError(t.pleaseAddTruck);
        return;
      }
      if (trucks.length > 0) {
        for (let i = 0; i < trucks.length; i++) {
          const truck = trucks[i];
          if (!truck.truck_number.trim()) {
            setError(`${t.enterTruckNumber}${i + 1}`);
            return;
          }
          if (truck.loads_hauled <= 0) {
            setError(`${t.enterLoadsHauled}${i + 1}`);
            return;
          }
          if (truck.hours_worked <= 0) {
            setError(`${t.enterHoursWorked}${i + 1}`);
            return;
          }
        }
      }
    }

    setError(null);
    setStep(step + 1);
    const mc = document.querySelector('.main-content');
    if (mc) mc.scrollTo(0, 0); else window.scrollTo(0, 0);
  };

  // Submit report
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Final validation
    if (!selectedJobId) {
      setError(t.mustBeClockedIn);
      return;
    }

    if (!workDescription.trim()) {
      setError(t.pleaseDescribeWork);
      return;
    }

    // Skip supplier validation if "No Material Today" is selected
    if (!noMaterialToday && !selectedSupplierId && !newSupplierName.trim()) {
      setError(t.pleaseSelectPlant);
      return;
    }

    // Validate trucks (only if any trucks were added)
    if (trucks.length > 0) {
      for (let i = 0; i < trucks.length; i++) {
        const truck = trucks[i];
        if (!truck.truck_number.trim()) {
          setError(`${t.enterTruckNumber}${i + 1}`);
          return;
        }
        if (truck.loads_hauled <= 0) {
          setError(`${t.enterLoadsHauled}${i + 1}`);
          return;
        }
        if (truck.hours_worked <= 0) {
          setError(`${t.enterHoursWorked}${i + 1}`);
          return;
        }
      }
    }

    // Submit the report
    setIsSubmitting(true);
    setError(null);

    try {
      if (!user) {
        setError(t.userNotLoggedIn);
        return;
      }

      // Prepare data object (non-file fields)
      // Get report date in EST timezone (night work window shifts date forward)
      const localDateStr = getReportDateString();

      const reportData: any = {
        job_id: selectedJobId,
        report_date: localDateStr,
        work_description: workDescription,
        job_needs: jobNeeds,
        crew_took_lunch: settings.lunchButtonVisible ? (crewTookLunch ? 1 : 0) : null,
        lunch_mode: settings.lunchMode, // 'auto' or 'manual' - backend uses this to determine if NO_LUNCH_DATES entry should be created
        tickets_scanned: ticketsScanned,
        asphalt_ticket_count: asphaltTicketCount,
        notes: notes,
        trucks: JSON.stringify(trucks),
        // Dump trailer (weather is auto-populated at 8 PM cron)
        dump_trailer_used: dumpTrailerUsed ? 1 : 0,
        total_trucks: totalTrucks ? parseInt(totalTrucks) : null,
        total_loads: totalLoads ? parseInt(totalLoads) : null
      };

      // Multi-job support: include job visits (weather is auto-populated at 8 PM cron)
      if (isMultiJob && jobVisits.length > 1) {
        reportData.job_visits = JSON.stringify(jobVisits.map((visit, index) => ({
          job_id: visit.job_id,
          arrival_time: visit.arrival_time,
          departure_time: visit.departure_time,
          display_order: index
        })));
      }

      // Plant / Material supplier
      if (noMaterialToday) {
        reportData.no_material_today = true;
        // Don't set material_supplier_id or new_supplier_name
      } else if (newSupplierName.trim()) {
        reportData.new_supplier_name = newSupplierName.trim();
      } else if (selectedSupplierId) {
        reportData.material_supplier_id = selectedSupplierId;
      }

      // Photos taken in form are now uploaded as standalone asphalt photos via /api/asphalt-reports/photos
      // They're already in ASPHALT_REPORT_PHOTOS with job_id - orphan linking will set report_id
      // We only need to handle photos that FAILED to upload
      const uploadedPhotos = photos.filter(p => p.uploadStatus === 'uploaded');
      const failedPhotos = photos.filter(p => p.uploadStatus !== 'uploaded');

      // Note: We do NOT send pre_uploaded_photos anymore since photos are already standalone
      // in ASPHALT_REPORT_PHOTOS and will be linked via orphan linking query

      // Prepare photo captions (for photos that need to be uploaded now)
      const failedPhotoCaptions = failedPhotos.map(p => p.caption || '');
      if (failedPhotoCaptions.length > 0) {
        reportData.photo_captions = JSON.stringify(failedPhotoCaptions);
      }

      // Only add files for photos that failed to pre-upload
      const files: { file: Blob; filename: string; fieldName: string }[] = [];
      for (const photo of failedPhotos) {
        files.push({
          file: photo.file,
          filename: photo.file.name || `photo_${Date.now()}.jpg`,
          fieldName: 'photos'
        });
      }

      console.log(`📤 Submitting: ${uploadedPhotos.length} already uploaded (will be linked), ${failedPhotos.length} need upload`);

      // Note: Tickets are now scanned via TruckTickets module
      // The asphaltTicketCount is fetched from API and included for reference
      // This represents tickets already uploaded via TruckTickets

      // Queue for upload with proper signature
      await uploadQueueService.addToQueue(
        'asphalt_report',
        reportData,
        files,
        user.employee_id,
        selectedJobId || undefined
      );

      // Show success immediately - no waiting for upload tracking
      setSuccess(true);
      setUploadStatus('completed');
      clearDraftFromLocalStorage();

    } catch (err) {
      console.error('Error submitting asphalt report:', err);
      setError(t.failedToSubmit);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state - full screen overlay
  if (jobLoading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}>
        <Loader2
          size={48}
          style={{
            color: 'white',
            animation: 'spin 1s linear infinite'
          }}
        />
        <div style={{
          color: 'white',
          fontSize: 18,
          marginTop: 20,
          fontWeight: '500'
        }}>
          {t.loading}
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Error state (not authorized)
  if (error && !selectedJobId) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '16px', padding: '24px' }}>
        <AlertCircle size={56} color="#dc2626" />
        <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', textAlign: 'center' }}>{t.accessDenied}</h2>
        <p style={{ color: '#6b7280', fontSize: '18px', textAlign: 'center', maxWidth: '400px' }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: '400px', minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Submitting overlay */}
      {isSubmitting && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <Loader2
            size={48}
            style={{
              color: 'white',
              animation: 'spin 1s linear infinite'
            }}
          />
          <div style={{
            color: 'white',
            fontSize: 18,
            marginTop: 20,
            fontWeight: '500'
          }}>
            {language === 'es' ? 'Enviando reporte...' : 'Submitting report...'}
          </div>
        </div>
      )}

      {/* Note: Job mismatch modal is handled globally by GlobalMismatchChecker in App.tsx */}

      {/* Header */}
      <div style={{ background: 'white', padding: '20px', borderBottom: '2px solid #e5e7eb' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <FileText size={32} color="#dc2626" />
          <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0, color: '#111827' }}>{t.asphaltReport}</h1>
        </div>

        {/* Night Shift Banner */}
        {getNightWorkStatus() && (
          <div style={{
            backgroundColor: '#1e1b4b',
            color: '#c4b5fd',
            padding: '10px 16px',
            borderRadius: 8,
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 15,
            fontWeight: 600
          }}>
            <span style={{ fontSize: 20 }}>&#127769;</span>
            {language === 'es' ? 'Reporte de Turno Nocturno' : 'Night Shift Report'}
          </div>
        )}

        {/* Online/Offline indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {isOnline ? (
            <>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#10b981' }} />
              <span style={{ fontSize: '16px', color: '#6b7280' }}>{t.online}</span>
            </>
          ) : (
            <>
              <CloudOff size={18} color="#ef4444" />
              <span style={{ fontSize: '16px', color: '#ef4444' }}>{t.offline}</span>
            </>
          )}
          {uploadQueueCount > 0 && (
            <span style={{ fontSize: '16px', color: '#f59e0b', marginLeft: '12px', fontWeight: '600' }}>
              {uploadQueueCount} {uploadQueueCount > 1 ? t.reportsQueued : t.reportQueued}
            </span>
          )}
        </div>

        {/* Step indicators - only show when past step 0 */}
        {step > 0 && (
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <div style={{
              flex: 1,
              padding: '10px 8px',
              backgroundColor: step === 1 ? '#dc2626' : step > 1 ? '#10b981' : '#e5e7eb',
              color: step >= 1 ? 'white' : '#6b7280',
              borderRadius: '8px',
              textAlign: 'center',
              fontWeight: '700',
              fontSize: '14px'
            }}>
              1. {t.workInfo}
            </div>
            {/* Only show Dump Trucks step if tickets were NOT scanned */}
            {ticketsScanned !== true && (
              <div style={{
                flex: 1,
                padding: '10px 8px',
                backgroundColor: step === 2 ? '#dc2626' : step > 2 ? '#10b981' : '#e5e7eb',
                color: step >= 2 ? 'white' : '#6b7280',
                borderRadius: '8px',
                textAlign: 'center',
                fontWeight: '700',
                fontSize: '14px'
              }}>
                2. {t.dumpTrucks}
              </div>
            )}
            <div style={{
              flex: 1,
              padding: '10px 8px',
              backgroundColor: step === 3 ? '#dc2626' : '#e5e7eb',
              color: step === 3 ? 'white' : '#6b7280',
              borderRadius: '8px',
              textAlign: 'center',
              fontWeight: '700',
              fontSize: '14px'
            }}>
              {ticketsScanned === true ? '2' : '3'}. {t.jobSitePhotos}
            </div>
          </div>
        )}
      </div>

      {/* Simple Success Modal - no waiting */}
      {success && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: '#d1fae5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <CheckCircle size={48} color="#10b981" strokeWidth={3} />
            </div>

            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#10b981',
              marginBottom: '12px'
            }}>
              {t.reportSaved}
            </h2>

            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              marginBottom: '28px',
              lineHeight: '1.5'
            }}>
              Your report has been submitted successfully!
            </p>

            <button
              onClick={() => {
                setSuccess(false);
                resetForm();
                navigate('/');
              }}
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '18px',
                fontWeight: '600',
                color: 'white',
                backgroundColor: '#10b981',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer'
              }}
            >
              {t.ok}
            </button>
          </div>
        </div>
      )}

      {/* Error Modal Popup */}
      {error && selectedJobId && (
        <div
          onClick={() => setError(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '28px',
              maxWidth: '400px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
              <AlertCircle size={56} color="#dc2626" />
              <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: 0, textAlign: 'center' }}>
                {t.error}
              </h2>
              <p style={{ color: '#374151', margin: 0, fontSize: '18px', textAlign: 'center', lineHeight: '1.6' }}>
                {error}
              </p>
              <button
                onClick={() => setError(null)}
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: 'white',
                  backgroundColor: '#dc2626',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  marginTop: '8px'
                }}
              >
                {t.ok}
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
        {/* Step 0: Ticket Scan Question */}
        {step === 0 && settings.ticketScanQuestion && (
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '28px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '26px', fontWeight: '700', marginBottom: '12px', color: '#111827' }}>{t.ticketScanQuestionTitle}</h2>

            {/* Show ticket count if any */}
            {asphaltTicketCount > 0 && (
              <div style={{
                padding: '16px 20px',
                backgroundColor: '#ecfdf5',
                border: '2px solid #10b981',
                borderRadius: '12px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <CheckCircle size={28} color="#10b981" />
                <span style={{ fontSize: '18px', fontWeight: '600', color: '#065f46' }}>
                  {asphaltTicketCount} {asphaltTicketCount === 1 ? t.ticketScannedToday : t.ticketsScannedToday}
                </span>
              </div>
            )}

            <p style={{ fontSize: '20px', fontWeight: '600', color: '#374151', marginBottom: '24px' }}>
              {t.didYouScanAllTickets}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <button
                type="button"
                onClick={() => {
                  setTicketsScanned(true);
                  setStep(1);
                  const mc = document.querySelector('.main-content');
                  if (mc) mc.scrollTo(0, 0); else window.scrollTo(0, 0);
                }}
                style={{
                  width: '100%',
                  padding: '24px',
                  fontSize: '20px',
                  fontWeight: '700',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <CheckCircle size={32} />
                {t.yesAllScanned}
                <span style={{ fontSize: '14px', fontWeight: '500', opacity: 0.9 }}>
                  {t.skipDumpTrucks}
                </span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setTicketsScanned(false);
                  setStep(1);
                  const mc = document.querySelector('.main-content');
                  if (mc) mc.scrollTo(0, 0); else window.scrollTo(0, 0);
                }}
                style={{
                  width: '100%',
                  padding: '24px',
                  fontSize: '20px',
                  fontWeight: '700',
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <AlertCircle size={32} />
                {t.noManualEntry}
                <span style={{ fontSize: '14px', fontWeight: '500', opacity: 0.9 }}>
                  {t.manualEntryRequired}
                </span>
              </button>
            </div>
          </div>
        )}

        {/* Step 1: Work Info */}
        {step === 1 && (
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '28px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            {/* Job Info (Read-only) - Shows all jobs visited today */}
            <div style={{ marginBottom: '28px', padding: '20px', backgroundColor: '#f3f4f6', borderRadius: '12px', border: '2px solid #e5e7eb' }}>
              <label style={{ display: 'block', fontSize: '18px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>
                {t.jobSite}{crewJobs.length > 1 ? 's' : ''}
              </label>
              {crewJobs.length > 1 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {crewJobs.map((job, idx) => (
                    <div key={job.job_id} style={{
                      padding: '10px 12px',
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db'
                    }}>
                      <span style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                        {job.job_name}
                      </span>
                      <span style={{ fontSize: '14px', color: '#6b7280', marginLeft: '8px' }}>
                        #{job.bradanna_job_number}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: '22px', fontWeight: '700', color: '#111827', margin: 0 }}>{selectedJobName}</p>
              )}
              <p style={{ fontSize: '15px', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>{t.autoSelectedFromTime}</p>
            </div>

            {/* Work Description */}
            <div style={{ marginBottom: '28px' }}>
              <label style={{ display: 'block', fontSize: '20px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>
                {t.workCompletedToday} *
              </label>
              <textarea
                value={workDescription}
                onChange={(e) => setWorkDescription(e.target.value)}
                placeholder={t.describeWhatAccomplished}
                required
                rows={5}
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '18px',
                  border: '2px solid #d1d5db',
                  borderRadius: '12px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {/* Material Supplier */}
            <div style={{ marginBottom: '28px' }}>
              <label style={{ display: 'block', fontSize: '20px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>
                {t.asphaltPlant} *
              </label>
              <CustomSelect
                value={noMaterialToday ? 'none' : (selectedSupplierId || '')}
                onChange={(value) => {
                  if (value === 'new') {
                    setShowNewSupplierInput(true);
                    setSelectedSupplierId(null);
                    setNoMaterialToday(false);
                  } else if (value === 'none') {
                    setNoMaterialToday(true);
                    setSelectedSupplierId(null);
                    setShowNewSupplierInput(false);
                    setNewSupplierName('');
                  } else {
                    setNoMaterialToday(false);
                    setSelectedSupplierId(value ? parseInt(value) : null);
                    setShowNewSupplierInput(false);
                    setNewSupplierName('');
                  }
                }}
                disabled={showNewSupplierInput}
                placeholder={t.selectPlant}
                options={[
                  { value: 'none', label: 'No Material Today' },
                  ...materialSuppliers.map(supplier => ({
                    value: supplier.subcontractor_id,
                    label: supplier.company_name + (supplier.preferred ? ' ★' : '')
                  }))
                ]}
              />
            </div>

            {/* Job Visit Timeline Section - Shows all jobs visited with times */}
            {crewJobs.length > 0 && (
              <div style={{ marginBottom: '28px' }}>
                <label style={{ display: 'block', fontSize: '18px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>
                  {language === 'es' ? 'Sitios Visitados Hoy' : "Today's Job Visits"}
                </label>
                <div style={{
                  backgroundColor: '#f0f9ff',
                  border: '2px solid #0ea5e9',
                  borderRadius: '12px',
                  overflow: 'hidden'
                }}>
                  {crewJobs.map((visit, index) => {
                    // Format times
                    const formatTime = (isoTime: string | null) => {
                      if (!isoTime) return '--:--';
                      const date = new Date(isoTime);
                      return date.toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                        timeZone: 'America/New_York'
                      });
                    };

                    return (
                      <div
                        key={visit.job_id}
                        style={{
                          padding: '14px 16px',
                          borderBottom: index < crewJobs.length - 1 ? '1px solid #bae6fd' : 'none'
                        }}
                      >
                        {/* Job name - full width on top */}
                        <div style={{
                          fontSize: '17px',
                          fontWeight: '700',
                          color: '#0c4a6e',
                          marginBottom: '6px'
                        }}>
                          {visit.job_name}
                          {visit.bradanna_job_number && (
                            <span style={{ fontSize: '14px', color: '#0369a1', fontWeight: '500', marginLeft: '8px' }}>
                              #{visit.bradanna_job_number}
                            </span>
                          )}
                        </div>

                        {/* Time and hours row */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          {/* Time range */}
                          <div style={{
                            fontSize: '15px',
                            fontWeight: '600',
                            color: '#0369a1'
                          }}>
                            🕐 {formatTime(visit.arrival_time)} - {formatTime(visit.departure_time)}
                          </div>

                          {/* Hours badge */}
                          <div style={{
                            fontSize: '15px',
                            fontWeight: '700',
                            color: '#0369a1',
                            backgroundColor: '#e0f2fe',
                            padding: '5px 12px',
                            borderRadius: '8px'
                          }}>
                            {(visit.total_hours || 0).toFixed(1)} hrs
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Dump Trailer Used Checkbox */}
            <div style={{ marginBottom: '28px' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '16px 20px',
                  backgroundColor: dumpTrailerUsed ? '#fff7ed' : '#f9fafb',
                  border: dumpTrailerUsed ? '2px solid #ea580c' : '2px solid #e5e7eb',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <input
                  type="checkbox"
                  checked={dumpTrailerUsed}
                  onChange={(e) => setDumpTrailerUsed(e.target.checked)}
                  style={{
                    width: '24px',
                    height: '24px',
                    accentColor: '#ea580c'
                  }}
                />
                <div>
                  <span style={{ fontSize: '18px', fontWeight: '700', color: '#374151' }}>
                    {t.dumpTrailerUsed}
                  </span>
                  <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0 0' }}>
                    {t.dumpTrailerDescription}
                  </p>
                </div>
              </label>
            </div>

            {/* Total Trucks & Total Loads - shown when material supplier selected and not dump trailer */}
            {!noMaterialToday && (selectedSupplierId || newSupplierName.trim()) && (
              <div style={{ marginBottom: '28px' }}>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '20px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>
                      {t.totalTrucks} {!dumpTrailerUsed ? '*' : ''}
                    </label>
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      value={totalTrucks}
                      onChange={(e) => setTotalTrucks(e.target.value)}
                      placeholder="0"
                      style={{
                        width: '100%',
                        padding: '16px 20px',
                        fontSize: '20px',
                        fontWeight: '600',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        backgroundColor: 'white',
                        textAlign: 'center',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '20px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>
                      {t.totalLoads} {!dumpTrailerUsed ? '*' : ''}
                    </label>
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      value={totalLoads}
                      onChange={(e) => setTotalLoads(e.target.value)}
                      placeholder="0"
                      style={{
                        width: '100%',
                        padding: '16px 20px',
                        fontSize: '20px',
                        fontWeight: '600',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        backgroundColor: 'white',
                        textAlign: 'center',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Crew Lunch - Conditional based on settings */}
            {settings.lunchButtonVisible && (
              <div style={{ marginBottom: '28px' }}>
                <label style={{ display: 'block', fontSize: '20px', fontWeight: '700', color: '#374151', marginBottom: '16px' }}>
                  {t.didCrewTakeLunch} *
                </label>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <button
                    type="button"
                    onClick={() => setCrewTookLunch(true)}
                    style={{
                      flex: 1,
                      padding: '18px',
                      fontSize: '20px',
                      fontWeight: '700',
                      backgroundColor: crewTookLunch === true ? '#10b981' : 'white',
                      color: crewTookLunch === true ? 'white' : '#374151',
                      border: crewTookLunch === true ? '3px solid #10b981' : '3px solid #d1d5db',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {t.yes}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCrewTookLunch(false)}
                    style={{
                      flex: 1,
                      padding: '18px',
                      fontSize: '20px',
                      fontWeight: '700',
                      backgroundColor: crewTookLunch === false ? '#ef4444' : 'white',
                      color: crewTookLunch === false ? 'white' : '#374151',
                      border: crewTookLunch === false ? '3px solid #ef4444' : '3px solid #d1d5db',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {t.no}
                  </button>
                </div>
              </div>
            )}

            {/* Job Needs for Tomorrow */}
            <div style={{ marginBottom: '28px' }}>
              <label style={{ display: 'block', fontSize: '20px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>
                {t.jobNeedsForTomorrow}
              </label>
              <textarea
                value={jobNeeds}
                onChange={(e) => setJobNeeds(e.target.value)}
                placeholder={t.whatMaterialsEquipment}
                rows={4}
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '18px',
                  border: '2px solid #d1d5db',
                  borderRadius: '12px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {/* Asphalt Tickets Scanned Today - Read-only summary */}
            {asphaltTicketCount > 0 && (
              <div style={{
                marginBottom: '28px',
                padding: '16px 20px',
                backgroundColor: '#ecfdf5',
                border: '2px solid #10b981',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <CheckCircle size={24} color="#10b981" />
                <span style={{ fontSize: '16px', fontWeight: '600', color: '#065f46' }}>
                  {asphaltTicketCount} {asphaltTicketCount === 1 ? t.ticketScannedToday : t.ticketsScannedToday}
                </span>
              </div>
            )}

            {/* Notes */}
            <div style={{ marginBottom: '28px' }}>
              <label style={{ display: 'block', fontSize: '20px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>
                {t.notesOptional}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t.additionalNotes}
                rows={4}
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '18px',
                  border: '2px solid #d1d5db',
                  borderRadius: '12px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {/* Next button */}
            <button
              type="button"
              onClick={goToNextStep}
              disabled={isTranslating}
              style={{
                width: '100%',
                padding: '20px',
                fontSize: '22px',
                fontWeight: '700',
                color: 'white',
                backgroundColor: isTranslating ? '#9ca3af' : '#dc2626',
                border: 'none',
                borderRadius: '14px',
                cursor: isTranslating ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px'
              }}
            >
              {isTranslating ? (
                <>
                  <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
                  {t.translating}
                </>
              ) : (
                <>
                  {ticketsScanned === true ? t.nextPhotos : t.nextDumpTrucks}
                  <ChevronRight size={28} />
                </>
              )}
            </button>
          </div>
        )}

        {/* Step 2: Dump Trucks */}
        {step === 2 && (
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '28px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '26px', fontWeight: '700', marginBottom: '12px', color: '#111827' }}>{t.dumpTrucksTitle}</h2>
            <p style={{ fontSize: '17px', color: '#6b7280', marginBottom: '24px' }}>{t.dumpTrucksDescription}</p>

            {trucks.length === 0 && (
              <div style={{
                color: '#6b7280',
                fontSize: '18px',
                textAlign: 'center',
                padding: '32px',
                backgroundColor: '#f9fafb',
                border: '2px dashed #d1d5db',
                borderRadius: '12px',
                marginBottom: '24px'
              }}>
                {t.noTrucksAdded}
              </div>
            )}

            {trucks.map((truck, index) => (
              <div key={index} style={{
                marginBottom: '20px',
                padding: '20px',
                backgroundColor: '#f9fafb',
                border: '2px solid #e5e7eb',
                borderRadius: '12px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: 0 }}>
                    {t.truckNumber}{index + 1}
                  </h3>
                  <button
                    type="button"
                    onClick={() => removeTruck(index)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 16px',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#dc2626',
                      backgroundColor: 'white',
                      border: '2px solid #dc2626',
                      borderRadius: '10px',
                      cursor: 'pointer'
                    }}
                  >
                    <Trash2 size={16} />
                    {t.remove}
                  </button>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '17px', fontWeight: '600', color: '#374151', marginBottom: '10px' }}>
                    {t.truckNumber} *
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={truck.truck_number}
                    onChange={(e) => updateTruck(index, 'truck_number', e.target.value)}
                    placeholder={t.truckNumberPlaceholder}
                    required
                    style={{
                      width: '100%',
                      padding: '14px',
                      fontSize: '18px',
                      border: '2px solid #d1d5db',
                      borderRadius: '10px'
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '17px', fontWeight: '600', color: '#374151', marginBottom: '10px' }}>
                      {t.loadsHauled} *
                    </label>
                    <input
                      type="number"
                      value={truck.loads_hauled || ''}
                      onChange={(e) => updateTruck(index, 'loads_hauled', parseInt(e.target.value) || 0)}
                      min="0"
                      required
                      style={{
                        width: '100%',
                        padding: '14px',
                        fontSize: '18px',
                        border: '2px solid #d1d5db',
                        borderRadius: '10px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '17px', fontWeight: '600', color: '#374151', marginBottom: '10px' }}>
                      {t.hoursWorked} *
                    </label>
                    <input
                      type="number"
                      value={truck.hours_worked || ''}
                      onChange={(e) => updateTruck(index, 'hours_worked', parseFloat(e.target.value) || 0)}
                      min="0"
                      step="0.5"
                      required
                      style={{
                        width: '100%',
                        padding: '14px',
                        fontSize: '18px',
                        border: '2px solid #d1d5db',
                        borderRadius: '10px'
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}

            {/* Add truck button - below trucks */}
            <button
              type="button"
              onClick={addTruck}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                padding: '18px',
                fontSize: '20px',
                fontWeight: '700',
                color: '#dc2626',
                backgroundColor: 'white',
                border: '2px solid #dc2626',
                borderRadius: '12px',
                cursor: 'pointer',
                marginBottom: '24px'
              }}
            >
              <Plus size={24} />
              {t.addDumpTruck}
            </button>

            {/* Navigation buttons */}
            <div style={{ display: 'flex', gap: '16px' }}>
              <button
                type="button"
                onClick={() => setStep(1)}
                style={{
                  flex: 1,
                  padding: '18px',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#dc2626',
                  backgroundColor: 'white',
                  border: '2px solid #dc2626',
                  borderRadius: '14px',
                  cursor: 'pointer'
                }}
              >
                ← {t.workInfo}
              </button>
              <button
                type="button"
                onClick={goToNextStep}
                style={{
                  flex: 1,
                  padding: '18px',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: 'white',
                  backgroundColor: '#dc2626',
                  border: 'none',
                  borderRadius: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {t.nextPhotos}
                <ChevronRight size={24} />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Job Site Photos */}
        {step === 3 && (
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '28px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '26px', fontWeight: '700', marginBottom: '24px', color: '#111827' }}>{t.jobSitePhotosTitle}</h2>

            {/* Pre-loaded Photos from PhotoGallery - Grouped by Job */}
            {preloadedPhotos.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <p style={{ fontSize: '14px', color: '#059669', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <CheckCircle size={16} /> {preloadedPhotos.length} photo{preloadedPhotos.length !== 1 ? 's' : ''} already uploaded today
                </p>

                {/* Group photos by job */}
                {(() => {
                  const byJob = preloadedPhotos.reduce((acc, photo) => {
                    const key = `${photo.job_id}-${photo.job_name}`;
                    if (!acc[key]) acc[key] = { job_name: photo.job_name, job_number: photo.bradanna_job_number, photos: [] };
                    acc[key].photos.push(photo);
                    return acc;
                  }, {} as Record<string, { job_name: string; job_number: string; photos: typeof preloadedPhotos }>);

                  return Object.entries(byJob).map(([key, group]) => (
                    <div key={key} style={{ marginBottom: '16px' }}>
                      <p style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500', marginBottom: '8px' }}>
                        {group.job_name} {group.job_number ? `#${group.job_number}` : ''}
                      </p>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: '8px'
                      }}>
                        {group.photos.map((photo) => (
                          <div key={photo.photo_id} style={{ position: 'relative' }}>
                            <img
                              src={photo.thumbnail_url || photo.photo_url}
                              alt={photo.caption || 'Photo'}
                              style={{
                                width: '100%',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '6px',
                                border: '2px solid #10b981'
                              }}
                            />
                            <div style={{
                              position: 'absolute',
                              bottom: '4px',
                              left: '4px',
                              width: '18px',
                              height: '18px',
                              borderRadius: '50%',
                              backgroundColor: '#10b981',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <CheckCircle size={12} color="white" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}

            {/* Photos Section - Tap to Add Style */}
            <div style={{ marginBottom: '32px' }}>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoInput}
                style={{ display: 'none' }}
              />

              {/* Tap to Add Photos Button */}
              <button
                type="button"
                onClick={() => isIOS() ? photoInputRef.current?.click() : setShowCamera(true)}
                disabled={photos.length >= 10}
                style={{
                  width: '100%',
                  padding: '32px 20px',
                  backgroundColor: '#f9fafb',
                  border: '2px dashed #d1d5db',
                  borderRadius: '16px',
                  cursor: photos.length >= 10 ? 'not-allowed' : 'pointer',
                  marginBottom: '20px',
                  opacity: photos.length >= 10 ? 0.6 : 1
                }}
              >
                <Camera style={{ width: 56, height: 56, color: '#9ca3af', margin: '0 auto 16px', display: 'block' }} />
                <p style={{ fontSize: 20, color: '#374151', marginBottom: 6, fontWeight: '600', textAlign: 'center' }}>
                  {photos.length >= 10 ? 'Max 10 photos' : t.takePhoto}
                </p>
                <p style={{ fontSize: 16, color: '#6b7280', textAlign: 'center', margin: 0 }}>{photos.length}/10 {language === 'en' ? 'new photos' : 'fotos nuevas'}</p>
              </button>

              {/* Photo Grid - 3 columns like DailyReports (new photos taken in form) */}
              {photos.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '10px',
                  marginBottom: '24px'
                }}>
                  {photos.map((photo, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img
                        src={photo.previewUrl}
                        alt={`Photo ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '120px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: `3px solid ${photo.uploadStatus === 'uploaded' ? '#10b981' : photo.uploadStatus === 'failed' ? '#ef4444' : '#3b82f6'}`
                        }}
                      />
                      {/* Upload status indicator */}
                      <div style={{
                        position: 'absolute',
                        bottom: '6px',
                        left: '6px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: photo.uploadStatus === 'uploaded' ? '#10b981' : photo.uploadStatus === 'failed' ? '#ef4444' : '#3b82f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {photo.uploadStatus === 'uploaded' ? (
                          <CheckCircle size={16} color="white" />
                        ) : photo.uploadStatus === 'failed' ? (
                          <AlertCircle size={16} color="white" />
                        ) : (
                          <Loader2 size={16} color="white" style={{ animation: 'spin 1s linear infinite' }} />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        style={{
                          position: 'absolute',
                          top: '6px',
                          right: '6px',
                          width: '30px',
                          height: '30px',
                          borderRadius: '50%',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '22px',
                          lineHeight: 1,
                          fontWeight: 'bold'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Navigation and Submit */}
            <div style={{ marginTop: '32px', marginBottom: '24px' }}>
              <button
                type="button"
                onClick={() => setStep(ticketsScanned === true ? 1 : 2)}
                style={{
                  width: '100%',
                  padding: '18px',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#dc2626',
                  backgroundColor: 'white',
                  border: '2px solid #dc2626',
                  borderRadius: '14px',
                  cursor: 'pointer',
                  marginBottom: '16px'
                }}
              >
                ← {ticketsScanned === true ? t.workInfo : t.dumpTrucks}
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '24px',
                fontSize: '22px',
                fontWeight: '700',
                color: 'white',
                backgroundColor: isSubmitting ? '#9ca3af' : '#dc2626',
                border: 'none',
                borderRadius: '14px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px'
              }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
                  {t.submitting}
                </>
              ) : (
                <>
                  <FileText size={24} />
                  {t.submitAsphaltReport}
                </>
              )}
            </button>
          </div>
        )}
      </form>

      {/* Camera Modal (Web) */}
      {showCamera && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'black', zIndex: 9999 }}>
          <CameraComponent
            onCapture={handleCameraPhoto}
            onClose={() => setShowCamera(false)}
          />
        </div>
      )}
    </div>
  );
};

export default AsphaltReports;
